/**
 * Route guard for the authenticated dashboard (`/app/**`).
 *
 * Everything under `/app` is customer data, so no `/app` route may render before
 * the session has been confirmed. This guard is what makes logout stick: after
 * `POST /auth/logout` the shared session drops to `anonymous`, every mounted
 * `/app` route re-renders through here, and the user is sent to `/login`. The
 * same applies to a direct URL typed into the address bar, a bookmark, a
 * restored back/forward page, and a tab that was already open when another tab
 * logged out.
 *
 * The API enforces authorisation independently — this guard is a UX and
 * data-exposure boundary in the browser, never the only line of defence.
 */
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "../hooks/useSession";
import { demoMode } from "../lib/config";
import { LoadingState } from "./states";

/** Where to send the user back to once they have signed in. */
export function nextParamFor(pathname: string, search: string): string {
  const target = `${pathname}${search}`;
  // Only ever round-trip an in-app path; never a full URL (open-redirect).
  const safe = target.startsWith("/app") ? target : "/app";
  return `/login?next=${encodeURIComponent(safe)}`;
}

export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { state, refresh } = useSession();
  const location = useLocation();

  switch (state.kind) {
    case "authenticated":
      return <>{children}</>;

    case "loading":
      return (
        <div className="ax-guard">
          <LoadingState label="Checking your session…" />
        </div>
      );

    case "not-configured":
      // A static preview build with no API URL. There is no session to check and
      // no customer data to leak: every panel renders its own honest
      // "not connected" state.
      return <>{children}</>;

    case "unavailable":
      // We could not confirm the session. Showing the dashboard here would mean
      // guessing that the user is signed in, so we don't.
      if (demoMode) return <>{children}</>;
      return (
        <div className="ax-guard" role="alert">
          <div className="state">
            <p className="state__title">We couldn’t confirm your sign-in</p>
            <p>
              ODIN’s API didn’t answer, so we can’t safely show your workspace. This is usually
              temporary.
            </p>
            <div className="row" style={{ gap: "0.75rem", marginTop: "1rem" }}>
              <button className="ax-btn ax-btn--primary" type="button" onClick={refresh}>
                Try again
              </button>
              <a className="ax-btn ax-btn--secondary" href="/login">
                Sign in
              </a>
            </div>
          </div>
        </div>
      );

    case "anonymous":
    default:
      return <Navigate to={nextParamFor(location.pathname, location.search)} replace />;
  }
}
